import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-col" }
const _hoisted_2 = { class: "flex grow items-center justify-center w-full" }
const _hoisted_3 = { class: "mr-2" }
const _hoisted_4 = { class: "font-sourcesanspro-semibold text-l2" }
const _hoisted_5 = { class: "flex items-center justify-center mb-2" }
const _hoisted_6 = { class: "flex items-center justify-center" }
const _hoisted_7 = { class: "text-m2" }
const _hoisted_8 = ["id"]

import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Capacitor } from '@capacitor/core';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { chevronBackOutline, downloadOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import TicketDetailItem from '@/shared/components/tickets/TicketDetailItem.vue';
import QueueTicketItem from '@/modules/queue/components/QueueTicketItem.vue';
import TopBar from '@/shared/components/TopBar.vue';
import useEvents from '@/modules/events/composables/useEvents';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useUtils from '@/shared/composables/useUtils';
import type { EventTicket } from '@/shared/interfaces/event';
import type { QueueSlot } from '@/modules/queue/composables/useQueue';
import { Event } from '@/shared/interfaces/event';

// Init

export default /*@__PURE__*/_defineComponent({
  __name: 'EventsTicketsDetailPage',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const eventId = +route.params.eventId;
const ticketIdToScroll = route.params.ticketId.toString();
const { downloadTicket, getEventTickets, updateTicket } = useEvents();
const { eventTickets, isFetchingEventTickets, refetchTickets } = getEventTickets(eventId);
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const { dragToScroll } = useUtils();
const downloadingTicket = ref({ ticketIndex: -1, isDownloading: false });

// onMountedHook
onMounted(async () => {
	// Update ticket if query params are present
	const shouldRefetchTickets = await updateTicket();
	if (shouldRefetchTickets) {
		refetchTickets();
	}

	// Scroll to ticket id
	setTimeout(() => {
		const ticketElement = document.getElementById(`ticket-${ticketIdToScroll}`);
		if (ticketElement) {
			ticketElement.scrollIntoView({
				behavior: 'smooth',
				inline: 'center',
			});
		} else {
			// Try to find queue ticket by checking parent containers
			const queueTickets = document.querySelectorAll('.queue-ticket-item');
			queueTickets.forEach((el) => {
				const slotId = (el as HTMLElement).dataset?.slotId;
				if (slotId && eventTickets.value?.some((t) => t.queue_slot?.id?.toString() === slotId && t.id.toString() === ticketIdToScroll)) {
					el.scrollIntoView({
						behavior: 'smooth',
						inline: 'center',
					});
				}
			});
		}
	}, 500);

	// Make content draggable to scroll on desktop
	if (!Capacitor.isNativePlatform()) {
		dragToScroll('dragToScrollElement');
	}
});

// Refetch tickets
const onRefetchTickets = () => {
	refetchTickets();
};

const router = useRouter();

// Check if ticket is a queue ticket
const isQueueTicket = (ticket: EventTicket) => {
	return !!ticket.queue_activity_id;
};

// Check if ticket has a booked slot
const hasBookedSlot = (ticket: EventTicket) => {
	return !!ticket.queue_slot;
};

// Convert event ticket to queue slot format for QueueTicketItem
const convertToQueueSlot = (ticket: EventTicket): QueueSlot | null => {
	if (!ticket.queue_slot) return null;

	// The queue_slot from API should already be in QueueSlot format
	// but we need to ensure it has all required fields
	return {
		...ticket.queue_slot,
		queue_activity: ticket.queue_activity || ticket.queue_slot.queue_activity,
		user:
			ticket.queue_slot.user || ticket.user
				? {
						id: ticket.user?.id || ticket.user_id,
						full_name: ticket.user?.full_name || ticket.participant?.full_name || '',
						email: ticket.user?.email || ticket.participant?.email || '',
					}
				: {
						id: ticket.user_id,
						full_name: '',
						email: '',
					},
	} as QueueSlot;
};

// Handle buy fastpass (for queue tickets)
const buyFastpass = (slotId: number) => {
	router.push(`/queue/upsell/${slotId}`);
};

// Handle book now for unbooked tickets
const handleBookNow = (ticket: EventTicket) => {
	if (!ticket.queue_activity_id) return;

	// Store event ticket ID(s) in sessionStorage
	const ticketIds = [ticket.id];
	sessionStorage.setItem('booking_event_ticket_ids', JSON.stringify(ticketIds));

	// Navigate to my-queues page
	router.push('/my-queues');
};

// Start downloading ticket
const startDownloadingTicket = async (event: Event, ticket: EventTicket, idx: number) => {
	downloadingTicket.value = { ticketIndex: idx, isDownloading: true };
	await downloadTicket(event, ticket);
	downloadingTicket.value.isDownloading = false;
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(_unref(IonContent), null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(TopBar, { class: "px-[34px] mb-4" }, {
          left: _withCtx(() => [
            _createVNode(_unref(IonButton), {
              class: "w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full",
              shape: "round"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_router_link, {
                  to: `/events/${eventId}/tickets`,
                  class: "flex items-center justify-center"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(IonIcon), {
                      icon: _unref(chevronBackOutline),
                      class: "ion-no-padding ion-no-margin text-black mr-[2px]"
                    }, null, 8, ["icon"])
                  ]),
                  _: 1
                }, 8, ["to"])
              ]),
              _: 1
            })
          ]),
          center: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('events.tickets.tickets_detail.title')), 1)
              ])
            ])
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", {
        id: "dragToScrollElement",
        class: _normalizeClass(["absolute top-[132px] left-0 flex w-[100%] max-w-[100%] invisible-scroller overflow-x-scroll", [
				{
					'justify-center': _unref(eventTickets) && _unref(eventTickets).length === 1,
				},
			]])
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(eventTickets), (ticket, idx) => {
          return (_openBlock(), _createElementBlock("div", {
            key: idx,
            class: _normalizeClass(["flex flex-col", [
					{
						'items-start justify-between': _unref(eventTickets) && _unref(eventTickets)?.length > 1,
						'items-center': _unref(eventTickets) && _unref(eventTickets).length === 1,
					},
				]])
          }, [
            (isQueueTicket(ticket) && hasBookedSlot(ticket) && convertToQueueSlot(ticket))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["flex flex-col", [
						{
							'ml-[34px] mr-4': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx === 0,
							'mr-8': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx === _unref(eventTickets)!.length - 1,
							'mr-4': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx !== _unref(eventTickets)!.length - 1,
						},
					]])
                }, [
                  _createElementVNode("div", _hoisted_5, [
                    (!ticket.event?.delayed_delivery_datetime || (ticket.event?.delayed_delivery_datetime && _unref(eventTickets) && _unref(eventTickets)[0].qr_code))
                      ? (_openBlock(), _createBlock(_unref(IonButton), {
                          key: 0,
                          class: "hover:bg-white hover:rounded-full",
                          size: "large",
                          shape: "round",
                          onClick: ($event: any) => (startDownloadingTicket(ticket.event, ticket, idx))
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_6, [
                              (downloadingTicket.value.ticketIndex === idx && downloadingTicket.value.isDownloading)
                                ? (_openBlock(), _createBlock(_unref(IonSpinner), {
                                    key: 0,
                                    name: "dots",
                                    class: "text-dark-blue mr-2"
                                  }))
                                : (_openBlock(), _createBlock(_unref(IonIcon), {
                                    key: 1,
                                    icon: _unref(downloadOutline),
                                    class: "text-dark-blue mr-2"
                                  }, null, 8, ["icon"])),
                              _createElementVNode("span", _hoisted_7, _toDisplayString(downloadingTicket.value.ticketIndex === idx && downloadingTicket.value.isDownloading ? _unref(t)('events.tickets.tickets_detail.downloading') : _unref(t)('events.tickets.tickets_detail.download')), 1)
                            ])
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", {
                    id: `ticket-${ticket.id}`,
                    class: "mb-6"
                  }, [
                    _createVNode(QueueTicketItem, {
                      slot: convertToQueueSlot(ticket)!,
                      "wallet-linked": !!ticket.wallet_id,
                      "hide-qr-code": ticket.user_id !== _unref(userProfile)?.id,
                      onBuyFastpass: buyFastpass
                    }, null, 8, ["slot", "wallet-linked", "hide-qr-code"])
                  ], 8, _hoisted_8)
                ], 2))
              : (_openBlock(), _createBlock(TicketDetailItem, {
                  onOnRefetchTickets: onRefetchTickets,
                  key: idx,
                  "qr-code": ticket.qr_code,
                  "hide-qr-code": ticket.user_id !== _unref(userProfile)?.id,
                  idx: idx,
                  "value-only": !!ticket.ticket_type?.value_only,
                  ticket: ticket,
                  "ticket-id": ticket.id.toString(),
                  "event-tickets": _unref(eventTickets),
                  name: ticket.participant?.full_name ? ticket.participant?.full_name : _unref(userProfile)?.full_name ? _unref(userProfile)?.full_name : '',
                  email: ticket.participant?.full_name ? ticket.participant?.email : _unref(userProfile)?.full_name ? _unref(userProfile)?.email : '',
                  "ticket-disabled-edit": ticket.disable_edit,
                  "ticket-type-color": ticket.ticket_type?.color ? ticket.ticket_type?.color : undefined,
                  "ticket-type-name": ticket.combo_ticket_type ? ticket.combo_ticket_type.name : ticket.ticket_type?.name ? ticket.ticket_type?.name : '',
                  "ticket-type-description": ticket.combo_ticket_type ? ticket.combo_ticket_type.description : ticket.ticket_type?.description ? ticket.ticket_type?.description : '',
                  "event-allow-edit-tickets": !!ticket.event.allow_edit_tickets,
                  "event-id": ticket.event_id,
                  "event-name": ticket.event.name,
                  "event-date": ticket.ticket_type?.custom_datetime ? ticket.ticket_type.custom_datetime : ticket.event.start_datetime,
                  venue: ticket.event.address,
                  "wallet-linked": ticket.wallet_id,
                  class: _normalizeClass([[
						{
							'ml-[34px] mr-4': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx === 0,
							'mr-8': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx === _unref(eventTickets)!.length - 1,
							'mr-4': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx !== _unref(eventTickets)!.length - 1,
						},
					], "mb-6"])
                }, null, 8, ["qr-code", "hide-qr-code", "idx", "value-only", "ticket", "ticket-id", "event-tickets", "name", "email", "ticket-disabled-edit", "ticket-type-color", "ticket-type-name", "ticket-type-description", "event-allow-edit-tickets", "event-id", "event-name", "event-date", "venue", "wallet-linked", "class"])),
            _createElementVNode("div", {
              class: _normalizeClass(["flex w-[300px] items-center justify-center", [
						{
							'ml-[34px] mr-4': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx === 0,
							'mr-4': _unref(eventTickets) && _unref(eventTickets)?.length > 1 && idx > 0,
						},
					]])
            }, null, 2)
          ], 2))
        }), 128))
      ], 2)
    ]),
    _: 1
  }))
}
}

})